import apiClient from 'apiClient/index';
import { CachePool } from 'helpers/CachePool/CachePool';
import { StorageCacheStore } from 'helpers/CachePool/StorageCacheStore';
import { DeduplicationPool } from 'helpers/DeduplicationPool/DeduplicationPool';
import {
  action, configure, makeAutoObservable, observable,
  runInAction,
} from 'mobx';
import {
  TraitBasicInfo, TraitCategory, TraitInfo, TraitsResults,
  TTraitCategories,
  TTraitDPFDataWithCategory,
} from 'stores/types/Traits';

import { Profile } from './types/Profiles';

const TRAITS_TTL_SECONDS = 86400;

configure({
  enforceActions: 'observed',
  isolateGlobalState: true,
  reactionRequiresObservable: true,
});

class TraitsStore {
  categories: TraitCategory[] = ['BEHAVIORAL', 'PHYSICAL'];
  traitsBasicInfo = new Map<string, TraitBasicInfo>();
  allTraitsBasicInfo:TraitBasicInfo[] = [];
  traitInfo = new Map<string, TraitInfo>();
  traitCategories: TTraitCategories = {
    BEHAVIORAL: [],
    PHYSICAL: [],
    UNDETERMINED: [],
  };

  traitsResults: TraitsResults | null = null;
  traitsResultsLoaded = false;
  loadingTraits = false;

  deduplicationPool = new DeduplicationPool();
  cachePool = new CachePool(new StorageCacheStore('TraitsStore'));

  traitsPDFData: TTraitDPFDataWithCategory | null = null;
  traitsPDFDataCache = new Map();

  constructor() {
    makeAutoObservable(this, {
      traitsBasicInfo: observable,
      allTraitsBasicInfo: observable,
      traitInfo: observable,
      traitCategories: observable,
      traitsResults: observable,
      traitsResultsLoaded: observable,
      loadingTraits: observable,
      traitsPDFData: observable,
      getTraitsResults: action,
      getTraitInfo: action,
      getTraitBasicInfo: action,
      setTraitCategories: action,
      setTraitCategoriesNew: action,
      addTraitInfo: action,
      setTraitsPDFData: action,
    });
  }

  getTraitsResults = async (testId: string) => {
    const key = `traits-${testId}`;
    await this.deduplicationPool.runTask(
      key,
      async () => {
        this.loadingTraits = true;
        const traitsData = await this.cachePool.runTask(
          key,
          async () => {
            const res = await apiClient.traits.getTraitsResults(testId);
            return res?.data ?? null;
          },
          TRAITS_TTL_SECONDS,
        );
        if (traitsData) {
          const traits = (traitsData.traits || [])
            .filter((trait) => !!trait.prediction)
            .filter((trait, i, all) => all.findIndex((t) => t.id === trait.id) === i);
          this.traitsResults = { traits };
          await this.getAllTraitBasicInfo();
          this.setTraitCategoriesNew();
        }
        this.traitsResultsLoaded = true;
        this.loadingTraits = false;
      },
    );
  };

  getTraitInfo = async (traitId: string) => {
    if (this.traitInfo.has(traitId)) {
      return this.traitInfo.get(traitId);
    }
    const traitResponse = await apiClient.traits.getTraitInfo(traitId);
    if (traitResponse?.data?.data?.traits?.length > 0) {
      const data = traitResponse.data.data.traits[0];
      data.references = data.references
        .filter((val, i) => i === data.references.findIndex((r) => r.html === val.html));
      this.traitInfo.set(traitId, data);
      return data;
    }
    return null;
  };

  getTraitBasicInfo = async (traitIds: string[]) => {
    const newTraitIds = (traitIds || []).filter((id) => !this.traitsBasicInfo.has(id));
    if (newTraitIds.length > 0) {
      const traitsResponse = await apiClient.traits.getTraitList(newTraitIds);
      if (traitsResponse?.data && traitsResponse.data.data) {
        this.addTraitInfo(traitsResponse.data.data.traits);
      }
    }
  };

  getAllTraitBasicInfo = async () => {
    await this.deduplicationPool.runTask(
      'traits-basic-info',
      async () => {
        const traitsResponse = await apiClient.traits.getAllTraitList();
        if (traitsResponse?.data && traitsResponse.data.data) {
          runInAction(() => {
            this.allTraitsBasicInfo = traitsResponse.data.data.traits;
          });
        }
      },
    );
  };

  addTraitInfo(traitsInfo: TraitBasicInfo[]) {
    const tempInfo = this.traitsBasicInfo;
    traitsInfo.forEach((info) => {
      if (!tempInfo.has(info.traitId)) {
        tempInfo.set(info.traitId, info);
      }
    });
    this.traitsBasicInfo = tempInfo;
  }

  setTraitCategories = () => {
    const traitCats: TTraitCategories = {
      BEHAVIORAL: [],
      PHYSICAL: [],
      UNDETERMINED: [],
    };
    if (this.traitsResults && this.traitsResults.traits) {
      this.traitsResults.traits.forEach((trait) => {
        const basicInfo = this.traitsBasicInfo.get(trait.id);
        if (basicInfo) {
          traitCats[basicInfo.traitType].push({
            id: trait.id,
            traitId: trait.id,
            name: basicInfo.name,
            imgUrl: basicInfo.image?.url,
            result: basicInfo.traitResults
              .find((r) => r.predictionId === trait.prediction)?.title || '',
          });
        }
      });
    }
    this.traitCategories = traitCats;
  };

  setTraitCategoriesNew = () => {
    const traitCats: Record<TraitCategory, any[]> = {
      BEHAVIORAL: [],
      PHYSICAL: [],
      UNDETERMINED: [],
    };

    const traitsResults = this.traitsResults?.traits || [];
    const allTraitsBasicInfo = this.allTraitsBasicInfo || [];

    /**
     * Sorting the data for sending the NA predictions to the bottom of the array
     * if the BE response is not formatted
     */
    traitsResults.sort((a, b) => {
      if (a.prediction === 'NA' && b.prediction !== 'NA') {
        return 1;
      } if (a.prediction !== 'NA' && b.prediction === 'NA') {
        return -1;
      }
      return 0;
    });

    allTraitsBasicInfo.forEach((traitInfo) => {
      const matchedResult = traitsResults.find((result) => result.id === traitInfo.traitId);
      if (matchedResult) {
        const extendedResult = {
          ...matchedResult,
          traitId: traitInfo.traitId,
          name: traitInfo.name,
          imgUrl: traitInfo.image?.url,
          result: traitInfo.traitResults
            .find((r) => r.predictionId === matchedResult.prediction)?.title || '',
        };

        const category = traitInfo.traitType ?? 'UNDETERMINED';
        if (category in traitCats) {
          traitCats[category as TraitCategory].push(extendedResult);
        }
      } else {
        traitCats.UNDETERMINED.push({
          id: traitInfo.traitId,
          traitId: traitInfo.traitId,
          name: traitInfo.name,
          imgUrl: traitInfo.image?.url,
          result: 'Undetermined',
        });
      }
    });

    Object.keys(traitCats).forEach((category) => {
      traitCats[category as TraitCategory].sort((a, b) => a.name.localeCompare(b.name));
    });

    this.traitCategories = traitCats;
  };

  setTraitsPDFData = async (petProfile: Profile) => {
    const key = `traits-dpf-${petProfile.testGUID}`;

    this.deduplicationPool.runTask(
      key,
      async () => {
        if (this.traitsPDFDataCache.has(petProfile.testGUID)) {
          runInAction(() => {
            this.traitsPDFData = this.traitsPDFDataCache.get(petProfile.testGUID)!;
          });
          return;
        }

        const traitIds = Object
          .values(this.traitCategories)
          .flatMap((traits) => traits.map((t) => t.id));

        if (traitIds.length === 0) return;

        const { data: { data: { traits: traitsInfoData } } } = await apiClient.traits
          .getTraitInfoForPDF({ traitId_in: traitIds });

        runInAction(() => {
          const allTraits = Object.entries(this.traitCategories).flatMap(([category, traits]) => (
            traits.map((trait) => ({
              ...trait,
              category: (!trait.result ? 'UNDETERMINED' : category) as TraitCategory,
            }))
          ));

          const pdfData = allTraits.reduce<TTraitDPFDataWithCategory>((result, trait) => {
            const info = traitsInfoData.find((t) => t.traitId === trait.traitId);

            const detailText = info?.traitResults
              .find((r) => r.predictionId === trait.prediction)?.detailText;

            result[trait.category].push({
              traitId: trait.traitId,
              name: trait.name,
              geneLocus: info?.geneLocus.join(', ').replaceAll(/<\/?i>/g, ''),
              result: trait.result,
              details: detailText?.replace('<Name>', petProfile.name),
              traitType: info?.traitType,
            });

            return result;
          }, { BEHAVIORAL: [], PHYSICAL: [], UNDETERMINED: [] });

          this.traitsPDFDataCache.set(petProfile.testGUID, pdfData);

          this.traitsPDFData = pdfData;
        });
      },
    );
  };
}

export default TraitsStore;
